// src/utils/axiosConfig.ts

import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { toastService, extractResponseMessage, extractErrorMessage } from "./toastService";

// Determine backend URL
const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  "http://localhost:5001/api" ||
  "https://hrm-martechsol-server-dbba68b93770.herokuapp.com/api";

const axiosInstance = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
  timeout: 50000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Extend the request config to include custom properties
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipSuccessToast?: boolean;
  skipErrorToast?: boolean;
  customSuccessMessage?: string;
  customErrorMessage?: string;
}

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  }
);

// Response Interceptor for global toast notifications
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const config = response.config as CustomAxiosRequestConfig;

    // Skip toast for GET requests or if explicitly disabled
    if (config.skipSuccessToast || config.method?.toLowerCase() === "get") {
      return response;
    }

    // Try to extract message from response
    const message = config.customSuccessMessage || extractResponseMessage(response.data);

    // Show success toast if there's a meaningful message
    if (message) {
      toastService.success(message);
    }

    return response;
  },
  (error) => {
    const config = error.config as CustomAxiosRequestConfig;

    // Log error for debugging
    console.error(
      "Response Error:",
      error.response ? error.response.data : error
    );

    // Skip toast if explicitly disabled
    if (config?.skipErrorToast) {
      return Promise.reject(error);
    }

    // Extract and show error message
    const errorMessage = config?.customErrorMessage || extractErrorMessage(error);
    toastService.error(errorMessage);

    // Handle specific scenarios
    if (error.response?.status === 401) {
      // Check if we're not already on the login page
      const currentPath = window.location.pathname;
      if (currentPath !== "/" && currentPath !== "/login") {
        // Clear any stored auth data and redirect to login
        // This will be handled by the PrivateRoute component
        console.log("Session expired - user needs to re-authenticate");
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to make requests without automatic toast
 * Useful for background operations or when you want to handle messages manually
 */
export const silentRequest = {
  get: <T>(url: string, config?: any) =>
    axiosInstance.get<T>(url, { ...config, skipSuccessToast: true, skipErrorToast: true }),
  post: <T>(url: string, data?: any, config?: any) =>
    axiosInstance.post<T>(url, data, { ...config, skipSuccessToast: true, skipErrorToast: true }),
  put: <T>(url: string, data?: any, config?: any) =>
    axiosInstance.put<T>(url, data, { ...config, skipSuccessToast: true, skipErrorToast: true }),
  patch: <T>(url: string, data?: any, config?: any) =>
    axiosInstance.patch<T>(url, data, { ...config, skipSuccessToast: true, skipErrorToast: true }),
  delete: <T>(url: string, config?: any) =>
    axiosInstance.delete<T>(url, { ...config, skipSuccessToast: true, skipErrorToast: true }),
};

/**
 * Helper function to make requests with custom messages
 */
export const requestWithMessage = {
  post: <T>(url: string, data?: any, successMessage?: string, errorMessage?: string, config?: any) =>
    axiosInstance.post<T>(url, data, { ...config, customSuccessMessage: successMessage, customErrorMessage: errorMessage }),
  put: <T>(url: string, data?: any, successMessage?: string, errorMessage?: string, config?: any) =>
    axiosInstance.put<T>(url, data, { ...config, customSuccessMessage: successMessage, customErrorMessage: errorMessage }),
  delete: <T>(url: string, successMessage?: string, errorMessage?: string, config?: any) =>
    axiosInstance.delete<T>(url, { ...config, customSuccessMessage: successMessage, customErrorMessage: errorMessage }),
};

export default axiosInstance;
